import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../api";
import type { MembershipKind, MembershipRole, Person } from "./memberships";
import type { ID } from "../id";

// Mirrors the backend's personListItem, which embeds personDTO — so this
// is a Person plus the two registry aggregates, not a separate shape.
export interface PersonListItem extends Person {
  teams_count: number;
  teams_label?: string;
  // The newest season the person holds a membership in (the one the teams
  // come from), "" when none. Two look-alike records whose seasons follow
  // one another read as one person re-registered under a new record.
  last_season: string;
  // The linked User's login address, "" when the person has no account.
  // Read-only: e-mail is owned by User and changed through the user
  // endpoints, never through a Person edit.
  login_email: string;
  // Whether the person holds a registered membership in the ACTIVE season —
  // i.e. whether they read „Člen" or „Nečlen" right now. Per-season by
  // design: someone who was a member last season and has not re-registered
  // is a Nečlen today, so any UI showing this must name the season.
  is_member: boolean;
}

export interface PersonListResponse {
  items: PersonListItem[];
  total: number;
  page: number;
  per_page: number;
  // The ordering the server actually applied, canonically ("last_name" /
  // "-cmf_id"). An unknown ?sort= falls back to the default rather than
  // failing, so this is how a stale bookmark announces itself.
  sort: string;
}

export interface PersonListParams {
  q?: string;
  // "1" = holds a registered membership in the active season, "0" = does not.
  member?: "1" | "0" | "";
  // Narrows to one team's current soupiska (registered members, active
  // season). Used by the leader picker, which needs login_email — the roster
  // endpoint does not carry it.
  team_id?: ID;
  page?: number;
  per_page?: number;
  // One signed field from the server's allow-list (internal/handlers/api/
  // persons.go `personSort`): "cmf_id", "-birth_date", … Only base-query
  // columns are sortable — the filled-in aggregates (teams, login e-mail,
  // membership) are not columns of that query.
  sort?: string;
}

/** One person as a registry row, for the person detail dialog. */
export function usePersonDetail(id: ID | null) {
  return useQuery<PersonListItem>({
    queryKey: ["persons", "detail", id],
    queryFn: () => apiGet<PersonListItem>(`/persons/${id}`),
    enabled: id != null,
  });
}

/** One membership row of the person's history (GET /persons/:id/memberships). */
export interface PersonMembershipRow {
  id: ID;
  season_id: ID;
  season_code: string;
  season_active: boolean;
  team_id: ID;
  team_name: string;
  team_status: string;
  kind: MembershipKind;
  roles: MembershipRole[];
  valid_from: string;
  // Set only when the person was taken off that season's roster during the
  // season; a season ending closes nothing.
  valid_to: string | null;
}

export interface PersonLeadershipRow {
  team_id: ID;
  team_name: string;
  valid_from: string;
  valid_to: string | null;
}

export interface PersonMemberships {
  // Newest season first.
  memberships: PersonMembershipRow[];
  leaderships: PersonLeadershipRow[];
}

export function usePersonMemberships(id: ID | null) {
  return useQuery<PersonMemberships>({
    queryKey: ["persons", "memberships", id],
    queryFn: () => apiGet<PersonMemberships>(`/persons/${id}/memberships`),
    enabled: id != null,
  });
}

export function useGlobalPersons(params: PersonListParams) {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  if (params.member) search.set("member", params.member);
  if (params.team_id != null) search.set("team_id", String(params.team_id));
  if (params.page != null) search.set("page", String(params.page));
  if (params.per_page != null) search.set("per_page", String(params.per_page));
  if (params.sort) search.set("sort", params.sort);
  const qs = search.toString();
  return useQuery<PersonListResponse>({
    queryKey: ["persons", "list", params],
    queryFn: () => apiGet<PersonListResponse>(`/persons${qs ? `?${qs}` : ""}`),
    placeholderData: (prev) => prev,
  });
}
